/* ──────────────────────────────────────────────────────────────
   cityService.js
   Full city list for a (countryCode, stateName) pair.

   Source: `country-state-city` (offline dataset, ~150k cities).
     npm i country-state-city

   Why not Google:
     - AutocompleteService is a typeahead. Max 5 predictions, always
       ranked against an input string. No "list everything" mode.
     - Places Text Search caps at 20 per page / 60 total and ranks by
       prominence, so it will never return all ~2,700 cities in Texas.
     - Geocoding cannot enumerate the children of an admin area.
   Google is still used in this file, but only for the thing it is
   actually good at: resolving a typed state name -> the canonical
   state name + ISO code (see resolveStateFromGoogle below, optional).
   ────────────────────────────────────────────────────────────── */

import { City, State } from "country-state-city";

const cityCache = new Map();
const EMPTY = [];

export const normalize = (str) =>
  (str || "")
    .toLowerCase()
    .replace(/\s*state$/i, "")
    .replace(/[^a-z0-9]/g, "");

/** Google gives a state *name*; the dataset is keyed by ISO code. Bridge them. */
export function resolveStateCode(countryCode, stateName) {
  if (!countryCode || !stateName) return null;

  const target = normalize(stateName);
  const states = State.getStatesOfCountry(countryCode.toUpperCase()) || [];

  const exact = states.find((s) => normalize(s.name) === target);
  if (exact) return exact.isoCode;

  // "NCT of Delhi" vs "Delhi", "Washington, D.C." vs "District of Columbia", etc.
  const partial = states.find((s) => {
    const n = normalize(s.name);
    return n.includes(target) || target.includes(n);
  });
  if (partial) return partial.isoCode;

  // Last resort: the user may have typed the ISO code itself ("CA", "TX").
  const byCode = states.find(
    (s) => s.isoCode.toLowerCase() === String(stateName).trim().toLowerCase(),
  );
  return byCode ? byCode.isoCode : null;
}

/**
 * Every city in the state, sorted, de-duplicated by normalized name.
 * Synchronous + memoized, but exposed as a Promise so the component
 * never has to care where the data comes from.
 */
export function getCitiesOfState(countryCode, stateName) {
  const cc = (countryCode || "").toUpperCase();
  const key = `${cc}::${normalize(stateName)}`;
  if (cityCache.has(key)) return cityCache.get(key);

  const stateCode = resolveStateCode(cc, stateName);
  if (!stateCode) {
    cityCache.set(key, EMPTY);
    return EMPTY;
  }

  const raw = City.getCitiesOfState(cc, stateCode) || [];
  const seen = new Set();
  const list = [];

  raw.forEach((c) => {
    const k = normalize(c.name);
    if (!k || seen.has(k)) return;
    seen.add(k);
    list.push(c.name);
  });

  list.sort((a, b) => a.localeCompare(b));
  cityCache.set(key, list);
  return list;
}

/** Default loader handed to <MultiCitySelect fetchCities={...} />. */
export const defaultFetchCities = ({ countryCode, stateName }) =>
  Promise.resolve(getCitiesOfState(countryCode, stateName));

/* ──────────────────────────────────────────────────────────────
   OPTIONAL: Google top-up.

   Use this only if you must show places Google knows about that the
   offline dataset misses (new townships, renamed cities). It adds at
   most ~20 names and costs one Text Search request per state
   selection. It is NOT a substitute for the dataset.
   ────────────────────────────────────────────────────────────── */
export async function fetchCitiesWithGoogleTopUp({ countryCode, stateName }) {
  const base = getCitiesOfState(countryCode, stateName);

  try {
    if (!window.google?.maps?.importLibrary) return base;

    const { Place } = await window.google.maps.importLibrary("places");
    const { places } = await Place.searchByText({
      textQuery: `cities in ${stateName}`,
      fields: ["displayName", "formattedAddress"],
      includedType: "locality",
      maxResultCount: 20,
      ...(countryCode ? { region: countryCode.toLowerCase() } : {}),
    });

    const target = normalize(stateName);
    const known = new Set(base.map(normalize));
    const extra = [];

    (places || []).forEach((p) => {
      const name = (p.displayName || "").trim();
      const k = normalize(name);
      // Keep only places whose address actually sits inside the state.
      if (!k || known.has(k)) return;
      if (!normalize(p.formattedAddress).includes(target)) return;
      known.add(k);
      extra.push(name);
    });

    return extra.length
      ? [...base, ...extra].sort((a, b) => a.localeCompare(b))
      : base;
  } catch {
    return base;
  }
}
